import { defineStore } from 'pinia';
import { computed, onMounted, ref, toRef, watch, type ComputedRef, type Ref } from 'vue';
import { getSearchFormData, getCountryResorts } from '@/shared/api';
import { getMinPricesByCriterias } from '@/shared/api/getMinPricesByCriterias';
import { useTravelataPinia } from '@/shared/composables';
import { type CountryResortsUrl, type FormDataUrl, type IntelligentSearchUrl } from '@/shared/config/formData';
import { type LocalStorageKey } from '@/shared/config/localStorage';
import { getCurrentUrl, getUrlQuery } from '@/shared/lib/urlQuery';
import type {
  Country,
  DateRange,
  DepartureCity,
  NightsRange,
  Resort,
  SearchCriteriaParams,
  SearchFormData,
} from '@/shared/model';
import type { DestinationPrices, SearchCriteria, Selection, TouristsSelection } from './types';
import addSearch from '../lib/addSearch';
import { fromSearchCriteriaQuery } from '../lib/fromUrl';
import getLastSearchCriteria from '../lib/getLastSearchCriteria';
import { buildSearchCriteria, buildSearchCriteriaParams } from '../lib/searchCriteria';
import setLastSearchCriteria from '../lib/setLastSearchCriteria';

export type SearchCriteriaOptions = {
  formUrl: FormDataUrl;
  searchUrl?: IntelligentSearchUrl;
  resortsUrl?: CountryResortsUrl;
  /** Ключ, под которым читаются при инициализации и сохраняются при сабмите последние критерии. */
  lastCriteriaKey?: LocalStorageKey;
  withPrices?: boolean;
  /** Записывать поиск в историю (`tr_historySearch`). Только для тур-продукта. */
  withHistory?: boolean;
  /**
   * Гейт гидрации: пока форма закрыта, справочники не грузятся. Живёт вне стора —
   * это `ComputedRef` конкретного компонента, он не должен переживать его размонтирование.
   */
  isOpen?: ComputedRef<boolean>;
  withUrlCriteria?: boolean;
};

type HydrationOptions = Pick<
  SearchCriteriaOptions,
  'lastCriteriaKey' | 'resortsUrl' | 'withPrices' | 'withUrlCriteria'
>;

export interface SearchCriteriaModel {
  model: Ref<SearchCriteria>;
  prices: Ref<DestinationPrices>;
  formUrl: FormDataUrl;
  searchUrl: IntelligentSearchUrl | undefined;
  resortsUrl: CountryResortsUrl | undefined;
  saveSearch: (searchCriteria: SearchCriteria) => void;
  getFormData: () => Promise<{ model: SearchCriteria; formData: SearchFormData; params: SearchCriteria }>;
  formDataCache: Ref<SearchFormData | null>;
}

export type UseSearchCriteria = () => SearchCriteriaModel;

type CriteriasWithIndex = {
  requests: SearchCriteriaParams[];
  indexToId: Record<string, number>;
};

async function resolveAllResorts(
  country: Country | null | undefined,
  resorts: Resort[],
  resortsUrl?: string,
): Promise<Resort[]> {
  const countryResorts = await getCountryResorts(country, resortsUrl);
  const popularCountryResorts = countryResorts.filter((resort) => resort.isPopular);

  return [...popularCountryResorts, ...resorts];
}

function buildCountryCriterias(countries: (Country | null | undefined)[], params: SearchCriteria): CriteriasWithIndex {
  const indexToId: Record<string, number> = {};
  const requests = countries
    .filter((country): country is Country => Boolean(country && country.isPopular))
    .reduce<SearchCriteriaParams[]>((acc, country, index) => {
      indexToId[index] = country.id;
      acc.push(
        buildSearchCriteriaParams({
          ...params,
          destination: { country, kind: 'country' },
        }),
      );

      return acc;
    }, []);

  return { requests, indexToId };
}

function buildResortCriterias(
  resorts: Resort[],
  country: Country | null | undefined,
  params: SearchCriteria,
): CriteriasWithIndex {
  const indexToId: Record<string, number> = {};
  const requests = resorts.reduce<SearchCriteriaParams[]>((acc, resort, index) => {
    indexToId[index] = resort.id;
    acc.push(
      buildSearchCriteriaParams({
        ...params,
        destination: { country: country!, resorts: [resort], kind: 'resorts' },
      }),
    );

    return acc;
  }, []);

  return { requests, indexToId };
}

function applyMinPrices(
  prices: DestinationPrices,
  result: Record<number, number>,
  indexToId: Record<string, number>,
  section: 'country' | 'resort',
): DestinationPrices {
  const sectionPrices: Record<number, number> = { ...prices[section] };

  Object.entries(result).forEach(([index, value]) => {
    sectionPrices[indexToId[index]] = value;
  });

  return { ...prices, [section]: sectionPrices };
}

const STORE_ID_PREFIX = 'travelata/searchCriteria';

export function resolveSearchCriteriaStoreId(formUrl: FormDataUrl): string {
  return `${STORE_ID_PREFIX}:${formUrl}`;
}

function createSearchCriteriaStore(formUrl: FormDataUrl) {
  return defineStore(resolveSearchCriteriaStoreId(formUrl), () => {
    const prices = ref<DestinationPrices>({ country: {}, resort: {} });
    const dateRange = ref<DateRange | null>(null);
    const tourists = ref<TouristsSelection | null>(null);
    const nights = ref<NightsRange | null>(null);
    const fromCity = ref<DepartureCity | null>(null);
    const destination = ref<Selection | null>(null);
    const meals = ref<number[] | undefined>();
    const hotelCategories = ref<number[] | undefined>();
    const directFlight = ref<boolean | undefined>();
    const block = ref<boolean | undefined>();
    const formDataCache = ref<SearchFormData | null>(null);

    const model = computed<SearchCriteria>({
      get: () => {
        return {
          nights: nights.value,
          dateRange: dateRange.value,
          fromCity: fromCity.value,
          destination: destination.value,
          tourists: tourists.value,
          meals: meals.value,
          hotelCategories: hotelCategories.value,
          directFlight: directFlight.value,
          block: block.value,
        };
      },
      set: (searchCriteria: SearchCriteria) => {
        nights.value = searchCriteria.nights;
        dateRange.value = searchCriteria.dateRange;
        fromCity.value = searchCriteria.fromCity;
        destination.value = searchCriteria.destination;
        tourists.value = searchCriteria.tourists;
        meals.value = searchCriteria.meals;
        hotelCategories.value = searchCriteria.hotelCategories;
        directFlight.value = searchCriteria.directFlight;
        block.value = searchCriteria.block;
      },
    });

    const resolveMinPrices = async (
      params: SearchCriteria,
      formData: SearchFormData,
      resortsUrl: CountryResortsUrl | undefined,
    ): Promise<DestinationPrices> => {
      const resorts = params.destination?.kind === 'resorts' ? params.destination.resorts : [];
      const country = params.destination?.kind === 'none' ? null : params.destination?.country;
      const allResorts = await resolveAllResorts(country, resorts, resortsUrl);

      const { requests: countryRequests, indexToId: countryIndexToId } = buildCountryCriterias(
        [...formData.countries, country],
        params,
      );
      const { requests: resortRequests, indexToId: resortIndexToId } = buildResortCriterias(
        allResorts,
        country,
        params,
      );

      const [countriesResult, resortsResult] = await Promise.all([
        getMinPricesByCriterias(countryRequests),
        getMinPricesByCriterias(resortRequests),
      ]);

      const countryPrices = applyMinPrices(prices.value, countriesResult, countryIndexToId, 'country');

      return applyMinPrices(countryPrices, resortsResult, resortIndexToId, 'resort');
    };

    const setMinPricesByCriterias = async (
      params: SearchCriteria,
      formData: SearchFormData,
      resortsUrl: CountryResortsUrl | undefined,
    ): Promise<void> => {
      prices.value = await resolveMinPrices(params, formData, resortsUrl).catch(() => prices.value);
    };

    async function loadFormData({
      lastCriteriaKey,
      resortsUrl,
      withPrices,
      withUrlCriteria = true,
    }: HydrationOptions): Promise<void> {
      const data = await getFormData({
        lastCriteriaKey,
        resortsUrl,
        withPrices,
        withUrlCriteria,
      });
      model.value = data.model;
      if (!withPrices) return;
      setMinPricesByCriterias(data.params, data.formData, resortsUrl);
    }

    async function getFormData({
      lastCriteriaKey,
      resortsUrl,
      withUrlCriteria = true,
    }: HydrationOptions): Promise<{ model: SearchCriteria; formData: SearchFormData; params: SearchCriteria }> {
      const lastSearchCriteria = getLastSearchCriteria(lastCriteriaKey);
      const urlCriteria = withUrlCriteria ? fromSearchCriteriaQuery(getUrlQuery(getCurrentUrl())) : null;
      const formData = formDataCache.value ?? (await getSearchFormData(formUrl));
      if (!formDataCache.value) {
        formDataCache.value = formData;
      }
      if (formData.countries.length === 0) {
        return {
          model: model.value,
          formData,
          params: model.value,
        };
      }
      const params = await buildSearchCriteria(lastSearchCriteria, formData, resortsUrl, urlCriteria);
      return {
        model: {
          ...params,
          meals: meals.value ?? params.meals,
          hotelCategories: hotelCategories.value ?? params.hotelCategories,
          directFlight: directFlight.value ?? params.directFlight,
          block: block.value ?? params.block,
        },
        params,
        formData,
      };
    }

    let hydration: Promise<void> | null = null;
    function hydrate(options: HydrationOptions): Promise<void> {
      if (destination.value) return Promise.resolve();

      if (!hydration) {
        hydration = loadFormData(options).finally(() => {
          hydration = null;
        });
      }

      return hydration;
    }

    return {
      model,
      prices,
      nights,
      dateRange,
      fromCity,
      destination,
      tourists,
      meals,
      formDataCache,
      hotelCategories,
      directFlight,
      block,
      hydrate,
      getFormData,
    };
  });
}

type SearchCriteriaStoreDefinition = ReturnType<typeof createSearchCriteriaStore>;

const definitions = new Map<FormDataUrl, SearchCriteriaStoreDefinition>();

function getSearchCriteriaStoreDefinition(formUrl: FormDataUrl): SearchCriteriaStoreDefinition {
  const existing = definitions.get(formUrl);
  if (existing) return existing;

  const definition = createSearchCriteriaStore(formUrl);
  definitions.set(formUrl, definition);

  return definition;
}

export function resetSearchCriteriaStores(): void {
  definitions.clear();
}

export default function useSearchCriteria(options: SearchCriteriaOptions): SearchCriteriaModel {
  const { formUrl, searchUrl, resortsUrl, lastCriteriaKey, withPrices, withHistory, isOpen, withUrlCriteria } = options;
  const store = getSearchCriteriaStoreDefinition(formUrl)(useTravelataPinia());
  const hydrate = (): Promise<void> => store.hydrate({ lastCriteriaKey, resortsUrl, withPrices, withUrlCriteria });

  const getFormData = (): Promise<{ model: SearchCriteria; formData: SearchFormData; params: SearchCriteria }> =>
    store.getFormData({ lastCriteriaKey, resortsUrl, withPrices, withUrlCriteria });

  const saveSearch = (searchCriteria: SearchCriteria): void => {
    if (lastCriteriaKey) {
      setLastSearchCriteria(lastCriteriaKey, searchCriteria);
    }
    if (withHistory) {
      addSearch(searchCriteria);
    }
  };

  onMounted(() => {
    if (!isOpen) {
      hydrate();

      return;
    }

    watch(
      isOpen,
      (open) => {
        if (open) {
          hydrate();
        }
      },
      { immediate: true },
    );
  });

  return {
    model: toRef(store, 'model') as unknown as Ref<SearchCriteria>,
    prices: toRef(store, 'prices') as unknown as Ref<DestinationPrices>,
    formDataCache: toRef(store, 'formDataCache') as unknown as Ref<SearchFormData>,
    formUrl,
    searchUrl,
    resortsUrl,
    saveSearch,
    getFormData,
  };
}
